import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { getGameHistory, getTransactions } from "../lib/api";
import { formatCurrency, formatDate } from "../lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dices,
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle,
  Clock,
  XCircle,
  TrendingUp,
  TrendingDown,
} from "lucide-react";

const statusIcons = {
  pending: Clock,
  approved: CheckCircle,
  rejected: XCircle,
};

const statusColors = {
  pending: "text-yellow-500",
  approved: "text-primary",
  rejected: "text-destructive",
};

export default function History() {
  const [gameHistory, setGameHistory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [gamesRes, txRes] = await Promise.all([
        getGameHistory(),
        getTransactions(),
      ]);
      setGameHistory(gamesRes.data.games);
      setTransactions(txRes.data.transactions);
    } catch (error) {
      console.error("Failed to load history:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-heading font-bold mb-2">History</h1>
          <p className="text-muted-foreground">View your game results and transactions</p>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Tabs defaultValue="games" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-card">
              <TabsTrigger value="games" className="font-heading data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Dices className="w-4 h-4 mr-2" /> Games
              </TabsTrigger>
              <TabsTrigger value="transactions" className="font-heading data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <ArrowDownLeft className="w-4 h-4 mr-2" /> Transactions
              </TabsTrigger>
            </TabsList>

            {/* Games Tab */}
            <TabsContent value="games">
              <div className="glass-card rounded-xl p-6">
                {gameHistory.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Dices className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No games played yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {gameHistory.map((game, i) => (
                      <motion.div
                        key={game.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.03 }}
                        className={`flex items-center justify-between p-4 rounded-lg border ${
                          game.won
                            ? "bg-primary/5 border-primary/20"
                            : "bg-destructive/5 border-destructive/20"
                        }`}
                        data-testid={`game-history-${game.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl font-heading font-bold ${
                              game.won
                                ? "bg-primary/20 text-primary"
                                : "bg-destructive/20 text-destructive"
                            }`}
                          >
                            {game.total}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium capitalize">
                                Bet: {game.prediction}
                              </span>
                              <span className="text-muted-foreground">•</span>
                              <span className="text-sm text-muted-foreground font-mono">
                                🎲 {game.dice1} + {game.dice2}
                              </span>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {formatDate(game.created_at)}
                            </div>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="flex items-center gap-2 justify-end">
                            {game.won ? (
                              <TrendingUp className="w-4 h-4 text-primary" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-destructive" />
                            )}
                            <span
                              className={`font-mono font-bold ${
                                game.won ? "text-primary" : "text-destructive"
                              }`}
                            >
                              {game.won ? "+" : "-"}
                              {formatCurrency(game.won ? game.payout : game.bet_amount)}
                            </span>
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Bet: {formatCurrency(game.bet_amount)} • {game.multiplier}x
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions">
              <div className="glass-card rounded-xl p-6">
                {transactions.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <ArrowDownLeft className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No transactions yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((tx, i) => {
                      const StatusIcon = statusIcons[tx.status];
                      const isDeposit = tx.type === "deposit";

                      return (
                        <motion.div
                          key={tx.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.03 }}
                          className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-white/5"
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                isDeposit ? "bg-primary/20" : "bg-secondary/20"
                              }`}
                            >
                              {isDeposit ? (
                                <ArrowDownLeft className="w-5 h-5 text-primary" />
                              ) : (
                                <ArrowUpRight className="w-5 h-5 text-secondary" />
                              )}
                            </div>
                            <div>
                              <div className="font-medium capitalize">{tx.type}</div>
                              <div className="text-sm text-muted-foreground">
                                {formatDate(tx.created_at)}
                              </div>
                            </div>
                          </div>

                          <div className="text-right">
                            <div
                              className={`font-mono font-bold ${
                                isDeposit ? "text-primary" : "text-secondary"
                              }`}
                            >
                              {isDeposit ? "+" : "-"}
                              {formatCurrency(tx.amount)}
                            </div>
                            <div
                              className={`flex items-center gap-1 text-sm justify-end ${
                                statusColors[tx.status]
                              }`}
                            >
                              <StatusIcon className="w-3 h-3" />
                              <span className="capitalize">{tx.status}</span>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}
